import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { lineClamp } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import FavouriteToggle from '@stories/Components/FavouriteToggle/FavouriteToggle';
import styled from 'styled-components';
import { rgba } from 'polished';
const Container = styled.article `
  ${setCssVar('accentColor', brand.hta.green)};
  ${setCssVar('fgColor', brand.black)};
  ${setCssVar('bgColor', brand.hta.greenLight)};
  ${setCssVar('bgColorAlt', rgba(brand.hta.green, 0.55))};

  .theme-hta & {
    ${setCssVar('accentColor', brand.white)};
    ${setCssVar('fgColor', brand.white)};
    ${setCssVar('bgColor', brand.htaRebrand.green)};
  }

  background: ${cssVar('bgColor')};
  border-radius: 11px;
  color: ${cssVar('fgColor')};
  height: 100%;
  transition: background 0.3s;
  position: relative;
  

  @media ${from(Device.Tablet)} {
    min-height: 387px;
  }

  &:hover {
    background: ${cssVar('bgColorAlt')};
  }
`;
const BgArea = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: none;
  border-radius: 11px;

  .theme-hta & {
    display: block;
  }
`;
const Background = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  
`;
const Overlay = styled.div `
  transition: opacity, 0.3s;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  display: none;
  background-color: ${rgba(brand.black, 0.46)};
  opacity: 0;

  ${Container}:hover & {
    .theme-hta & {
      opacity: 1;
    }
  }

  .theme-hta & {
      display: block;
    }
`;
const Link = styled.a `
  display: flex;
  color: currentColor;
  height: 100%;
  text-decoration: none;

  @media ${until(Device.Tablet)} {
    flex-direction: column;
  }

  @media ${from(Device.Tablet)} {
    [data-featured='false'] & {
      flex-direction: column;
    }

    [data-featured='true'] & {
      flex-direction: row;
    }
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 22px;

  @media ${until(Device.Tablet)} {
    padding: 11px 11px 30px;
  }
`;
const Badge = styled.span `
  display: inline-block;
  background: ${cssVar('accentColor')};
  border-radius: 13px;
  ${fonts.sourceSansPro.bold};
  font-size: 16px;
  line-height: 20px;
  margin: 0 0 8px;
  padding: 3px 9px;

  .theme-hta & {
    color: ${brand.black};

    @media ${from(Device.Tablet)} {
      margin-bottom: 52px;
    }
  }

  @media ${from(Device.Tablet)} {
    border-radius: 16px;
    font-size: 20px;
    line-height: 24px;
    margin: 0 0 24px;
    padding: 4px 16px;
  }
`;
const Tag = styled.span `
  display: inline-block;
  ${fonts.sourceSansPro.regular};
  font-size: 14px;
  letter-spacing: 0.1em;
  line-height: 20px;
  text-transform: uppercase;

  .theme-hta & {
    margin-bottom: 9px;
  }

  [data-featured='true'] & {
    @media ${from(Device.Tablet)} {
      .theme-hta & {
        margin-bottom: 15px;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    font-size: 18px;
    line-height: 22px;
  }
`;
const Title = styled.h3 `
  ${lineClamp(3, 'inline')};

  [data-featured='true'] & {
    ${lineClamp(5, 'inline')};
  }

  @media ${until(Device.Tablet)} {
    ${fonts.sourceSansPro.semiBold};
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 16px;

    @media ${from(Device.Tablet)} {
      font-size: 22px;
      line-height: 32px;
    }

    @media ${from(Device.Desktop)} {
      font-size: 25px;
      line-height: 35px;
    }

    @media ${from(Device.DesktopLarge)} {
      font-size: 30px;
      line-height: 38px;
    }
  }

  @media ${from(Device.Tablet)} {
    [data-featured='false'] & {
      ${fonts.sourceSansPro.semiBold};
      font-size: 18px;
      line-height: 24px;
      margin-bottom: 16px;

      @media ${from(Device.Tablet)} {
        font-size: 22px;
        line-height: 32px;
      }

      @media ${from(Device.Desktop)} {
        font-size: 25px;
        line-height: 35px;
      }

      @media ${from(Device.DesktopLarge)} {
        font-size: 30px;
        line-height: 38px;
      }
    }

    [data-featured='true'] & {
      ${fonts.sourceSansPro.semiBold};
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 16px;

      @media ${from(Device.Tablet)} {
        font-size: 25px;
        line-height: 35px;
      }

      @media ${from(Device.Desktop)} {
        font-size: 35px;
        line-height: 45px;
      }

      @media ${from(Device.DesktopLarge)} {
        font-size: 45px;
        line-height: 55px;
      }
    }
  }

  ${Link}:focus &,
  ${Link}:hover & {
    text-decoration: underline;

    .theme-hta & {
      text-decoration: none;
    }
  }
`;
const Date = styled.time `
  display: inline-flex;
  align-items: center;
  ${fonts.sourceSansPro.bold};
  font-size: 16px;
  line-height: 22px;
  margin-top: auto;
  text-transform: uppercase;

  > svg {
    display: inline-block;
    height: 20px;
    width: 20px;
  }

  > span {
    display: inline-block;
    margin-left: 8px;
  }

  .theme-hta & {
    font-size: 18px;

    > span {
      margin-left: 11px;
    }

    @media ${from(Device.Tablet)} {
      font-size: 24px;

      > svg {
        height: 38px;
        width: 38px;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    font-size: 18px;
    line-height: 24px;

    > svg {
      height: 26px;
      width: 26px;
    }
  }
`;
const Location = styled.span `
  display: inline-flex;
  align-items: center;
  ${fonts.sourceSansPro.regular};
  font-size: 16px;
  line-height: 22px;
  margin: 16px 0 0;
  text-transform: uppercase;

  > svg {
    display: inline-block;
    height: 20px;
    width: 20px;
  }

  > span {
    display: inline-block;
    margin-left: 8px;
  }

  .theme-hta & {
    font-size: 18px;

    > span {
      margin-left: 11px;
    }

    @media ${from(Device.Tablet)} {
      font-size: 24px;

      > svg {
        height: 40px;
        width: 40px;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    font-size: 18px;
    line-height: 24px;
    margin: 22px 0 0;

    > svg {
      height: 26px;
      width: 26px;
    }
  }
`;
const StyledFavouriteToggle = styled(FavouriteToggle) `
  position: absolute;
  bottom: 4px;
  right: 4px;

  @media ${from(Device.Tablet)} {
    bottom: 15px;
    right: 15px;
  }
`;
export default {
    Background,
    Badge,
    BgArea,
    Container,
    ContentWrapper,
    Date,
    FavouriteToggle: StyledFavouriteToggle,
    Link,
    Location,
    Overlay,
    Tag,
    Title,
};
